import { TransactionsListInnerProps } from '@/app/components/TimeReporting/components/TimeSheet/components/DailySummary/components/TransactionsList/transactionsListModels';
import { ReportingGroupType } from '@/app/components/TimeReporting/components/TimeSheet/data';
import { useReportingTypeOptions } from '@/app/components/AutoDeduction/useReportingTypeOptions';
import moment from 'moment';
import { useMemo } from 'react';

export const useAutoDeduction = ({
  getFormValues,
  autoDeductionRule,
}: TransactionsListInnerProps) => {
  const formTimeBlocks = getFormValues('timeBlocks');

  // Fetch reporting type options using hooks
  const { reportingTypesOptions: correctionTypesData } = useReportingTypeOptions(
    [ReportingGroupType.TIMESHEET_CORRECTION_TYPE],
    null,
    true,
  );
  const { reportingTypesOptions: nonWorkTypesData } = useReportingTypeOptions(
    [ReportingGroupType.TIMESHEET_NON_WORK_TYPE],
    null,
    true,
  );
  const { reportingTypesOptions: workTypesData } = useReportingTypeOptions(
    [ReportingGroupType.TIMESHEET_WORK_TYPE],
    null,
    true,
  );

  // Extract options values from reporting types data
  const reportingTypes = useMemo(() => {
    const extractValues = (data: any[]) => {
      const options = data.length && data[0].options ? data[0].options : [];
      return options.map((o: any) => o.value);
    };

    return {
      correction: extractValues(correctionTypesData),
      nonWork: extractValues(nonWorkTypesData),
      work: extractValues(workTypesData),
    };
  }, [correctionTypesData, nonWorkTypesData, workTypesData]);

  const isEmptyObject = (obj: any) => Object.keys(obj).length === 0;

  const ruleChecks = useMemo(() => {
    // Helper functions and constants used within this memo
    const getTimeBlocksByTypes = (types: string[]) =>
      formTimeBlocks
        ? Object.entries(formTimeBlocks).filter(tb => {
            return !!tb[1].reportingType && types.includes(tb[1].reportingType);
          })
        : [];

    const ruleChecksDefaults = {
      firstDeductionId: '',
      hasDeduction: false,
      skipDeduction: false,
    };

    const dataRequirements: boolean =
      !!autoDeductionRule && !isEmptyObject(formTimeBlocks);

    if (!dataRequirements) {
      return { ...ruleChecksDefaults };
    }

    const getCompoundType = (group: ReportingGroupType, type: string) =>
      `${group}-${type}`;

    const deductionTBs = getTimeBlocksByTypes([
      getCompoundType(
        ReportingGroupType.TIMESHEET_CORRECTION_TYPE,
        autoDeductionRule.correctionCode,
      ),
    ]);

    const nonWorkMatchFound =
      getTimeBlocksByTypes([
        getCompoundType(
          ReportingGroupType.TIMESHEET_NON_WORK_TYPE,
          autoDeductionRule.nonWorkingCode,
        ),
      ]).length > 0;

    const getMilliseconds = (value: string) =>
      moment.duration(value).asMilliseconds();
    const workTime = getTimeBlocksByTypes(reportingTypes.work)
      .map(tb => getMilliseconds(tb[1].hours))
      .reduce((prev, cur) => prev + cur, 0);
    const thresholdExceeded =
      workTime >= getMilliseconds(autoDeductionRule.threshold);

    return {
      ...ruleChecksDefaults,
      firstDeductionId: deductionTBs.map(tb => tb[0])[0],
      hasDeduction: deductionTBs.length > 0,
      skipDeduction: nonWorkMatchFound || !thresholdExceeded,
    };
  }, [formTimeBlocks, autoDeductionRule, reportingTypes]);

  const isAutoDeductionRow = (timeBlockId: string) =>
    ruleChecks.hasDeduction &&
    ruleChecks.firstDeductionId === timeBlockId;

  const hasAutoDeductionLock = (timeBlockId: string) =>
    !ruleChecks.skipDeduction &&
    isAutoDeductionRow(timeBlockId);

  return {
    isAutoDeductionRow,
    hasAutoDeductionLock,
  };
};
